<template>
  <el-dialog
    :model-value="true"
    width="600"
    align-center
    :show-close="false"
    class="wrapper-dialog direct-chat-dialog max-md:max-w-[calc(100%-32px)]"
    @closed="emit('close')"
    @update:model-value="close"
  >
    <div class="flex h-[600px] max-h-[calc(100dvh-80px)] flex-col overflow-hidden rounded-[32px] bg-white">
      <!-- Шапка -->
      <div class="flex items-center gap-3 px-5 pb-4 pt-5">
        <div class="size-11 shrink-0 overflow-hidden rounded-full border-[1.5px] border-bgnew bg-bgnew">
          <img v-if="place.avatar" :src="place.avatar" alt="avatar" class="size-full object-cover" />
        </div>
        <div class="min-w-0 flex-1">
          <p class="truncate text-base font-bold leading-[1.3] text-txmain">{{ place.name }}</p>
          <div class="mt-1 flex min-w-0 items-center gap-2">
            <a
              v-if="handle"
              :href="place.url || undefined"
              target="_blank"
              rel="nofollow noopener noreferrer"
              class="!w-auto truncate text-xs leading-[1.3] text-primary"
              >{{ handle }}</a
            >
            <span
              v-if="place.format?.name"
              class="shrink-0 rounded-md border border-[#D9E5FF] px-1.5 py-0.5 text-[11px] leading-[1.3] text-txmain"
            >
              {{ place.format.name }}
            </span>
          </div>
        </div>
        <NuxtLink
          to="/dialogs"
          class="flex h-9 shrink-0 !w-auto items-center gap-1 rounded-xl bg-bgnew px-3 text-xs text-primary transition-colors hover:bg-primary/10 max-md:hidden"
        >
          Все диалоги
          <i class="fi fi-sr-angle-small-right text-sm leading-none text-primary" />
        </NuxtLink>
        <button
          type="button"
          class="flex size-9 shrink-0 items-center justify-center rounded-full bg-bgnew transition-colors hover:bg-primary/10"
          @click="close"
        >
          <ICross class="size-3.5 text-txmain/60" />
        </button>
      </div>

      <!-- Лента -->
      <div ref="listEl" class="ui-scrollbar mx-4 flex-1 overflow-y-auto rounded-[20px] bg-bgnew px-4 py-4">
        <div v-if="isLoading" class="flex h-full items-center justify-center text-sm text-txmain/60">Загрузка...</div>
        <div v-else-if="messages.length === 0" class="flex h-full flex-col items-center justify-center text-center">
          <span class="flex size-14 items-center justify-center rounded-2xl bg-primary/10">
            <i class="fi fi-sr-comment-alt text-2xl leading-none text-primary" />
          </span>
          <p class="mt-4 text-sm font-medium leading-[1.3] text-txmain">Запрос до заказа</p>
          <p class="mt-1.5 max-w-[320px] text-xs leading-[1.5] text-txmain/60">
            Уточните формат, сроки или креатив — переписка попадёт в «Диалоги» как запрос, без оформления сделки.
          </p>
        </div>
        <template v-else>
          <template v-for="(item, i) in messages" :key="item.id">
            <div v-if="isNewDay(i)" class="sticky top-0 z-[1] flex justify-center py-1 text-xs">
              <span class="rounded-full bg-white px-2.5 py-1 text-xs text-txmain">{{ formatDate(item.created, "DD MMMM") }}</span>
            </div>
            <div v-if="isSystemMessage(item)" class="my-4 flex justify-center">
              <ChatSystemEvent :message="item" :chat="chat" />
            </div>
            <div
              v-else
              class="flex items-end gap-x-2"
              :class="i && !isSeriesStart(i) ? 'mt-1' : 'mt-4'"
            >
              <div class="flex w-full items-end gap-x-2" :class="{ 'flex-row-reverse': itsMe(item) }">
                <div class="flex max-w-[calc(100%-100px)] flex-col space-y-0.5" :class="itsMe(item) ? 'items-end' : 'items-start'">
                  <!-- Имя, время и аватар — только у первого сообщения в серии -->
                  <div
                    v-if="isSeriesStart(i)"
                    class="mb-2.5 flex items-center justify-end gap-x-2"
                    :class="{ 'flex-row-reverse': !itsMe(item) }"
                  >
                    <div class="flex flex-col" :class="{ 'items-end': itsMe(item) }">
                      <p class="text-sm">{{ itsMe(item) ? "Вы" : place.name }}</p>
                      <p class="text-xs font-normal text-[#99ABBA]" :class="{ 'text-right': itsMe(item) }">
                        <UINewDatePopover :value="item.created" value-class="text-xs font-normal text-[#99ABBA]" is-reverse type="time" />
                      </p>
                    </div>
                    <img
                      v-if="itsMe(item) ? myAvatar : place.avatar"
                      :src="itsMe(item) ? myAvatar : place.avatar"
                      alt="Аватар"
                      class="size-8 rounded-full object-cover"
                    />
                    <div v-else class="size-8 rounded-full bg-white" />
                  </div>
                  <div
                    v-else-if="formatDate(item.created, 'DD.MM.YYYY HH:mm') !== formatDate(messages[i - 1].created, 'DD.MM.YYYY HH:mm')"
                    class="mt-2"
                  >
                    <UINewDatePopover :value="item.created" value-class="text-xs font-normal text-[#99ABBA]" is-reverse type="time" />
                  </div>
                  <div
                    class="w-fit whitespace-break-spaces rounded-2xl p-3 text-sm [overflow-wrap:anywhere]"
                    :class="itsMe(item) ? 'bg-primary text-white' : 'bg-white text-txmain'"
                  >
                    <ChatMessageText :text="item.content" :mentions="publicationMentions" :dark="itsMe(item)" />
                    <ChatAttachments :files="item.files" :dark="itsMe(item)" :class="{ 'mt-2': item.content }" />
                  </div>
                </div>
              </div>
            </div>
          </template>
          <p v-if="isTyping" class="mt-2 text-xs text-[#99ABBA]">{{ place.name }} печатает...</p>
        </template>
      </div>

      <!-- Ввод. relative — от него позиционируется список выбранных файлов -->
      <div class="relative flex items-end gap-2 px-4 pb-4 pt-3">
        <ChatAttachButton ref="attachments" side="advertiser" class="direct-chat-dialog__attach" />
        <ChatMentionInput
          v-model="text"
          class="direct-chat-dialog__input"
          :placeholder="isMobile ? 'Ваш вопрос…' : 'Вопрос по формату, срокам или креативу…'"
          :mentions="publicationMentions"
          submit-on-enter
          @input="onInput"
          @submit="send"
        />
        <button
          type="button"
          class="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="!canSend"
          @click="send"
        >
          <span class="text-sm text-white max-md:hidden">Отправить</span>
          <i class="fi fi-sr-paper-plane-top text-base leading-none !text-white" />
        </button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
  import dayjs from "dayjs";
  import "dayjs/locale/ru";
  import ChatAdvertiserService from "~/services/ChatAdvertiserService";
  import PlaceService from "~/services/PlaceService";
  import { ApiKey } from "~/common/API/baseApi";
  import { ChatUserType, type IChatFile, type IMessageChat } from "~/interfaces/chat";
  import {
    getPlaceChatSocial,
    getPublicationMentions,
    isSystemMessage,
    pickMessageMeta,
    unwrapChat,
  } from "~/common/chatChannels";
  import { useSocketChat } from "~/composables/useSocketChat";
  import ICross from "~/components/UINew/icons/ICross.vue";
  import { useScreenSize } from "@/helpers/isMobile";
  import { useUserStore } from "~/store/UserModule";

  interface DirectChatPlace {
    id: number;
    name: string;
    avatar?: string | null;
    url?: string | null;
    social?: string | null;
    // В списке каталога владельца нет — тогда подтягиваем карточку канала
    userId?: number | null;
    format?: { name: string } | null;
  }

  const props = defineProps<{ place: DirectChatPlace }>();
  const emit = defineEmits<{ close: [] }>();

  const { isMobile } = useScreenSize();

  const advertiserId = +localStorage.getItem("useUserStore__id")!;

  const { socket, isTyping, isListeningMessages, connect, joinChat, disconnect, setupTypingTimer, handleTyping, handleNewMessage } =
    useSocketChat();

  const publisherId = ref<number | null>(props.place.userId ?? null);
  const chat = ref<any>(null);

  // Канал для канального чата: id + соцсеть (id каналов в разных соцсетях независимы)
  const channelKey = () => ({ placeId: props.place.id, social: getPlaceChatSocial(props.place) });
  const messages = ref<IMessageChat[]>([]);
  const text = ref("");
  const isLoading = ref(true);
  const isSending = ref(false);
  const listEl = ref<HTMLElement | null>(null);

  // Публикации канала для упоминания через @ и тегов в сообщениях
  const publicationMentions = computed(() => getPublicationMentions(chat.value, messages.value));

  const handle = computed(() => (props.place.url || "").replace("https://t.me/", "@").replace("https:/t.me/", "@"));

  const itsMe = (item: IMessageChat) => item.userType === ChatUserType.ADVERTISER;

  const formatDate = (value: string, format: string) =>
    dayjs(value).add(dayjs().utcOffset(), "seconds").locale("ru").format(format);

  const userModule = useUserStore();
  const myAvatar = computed<string | null>(() => userModule.user?.avatar ?? null);

  const isSeriesStart = (i: number) =>
    i === 0 ||
    isNewDay(i) ||
    isSystemMessage(messages.value[i - 1]) ||
    itsMe(messages.value[i - 1]) !== itsMe(messages.value[i]);

  const isNewDay = (i: number) =>
    i === 0 || formatDate(messages.value[i].created, "DD.MM.YYYY") !== formatDate(messages.value[i - 1].created, "DD.MM.YYYY");

  const close = () => emit("close");

  const attachments = ref<{ files: IChatFile[]; isUploading: boolean; reset: () => void } | null>(null);

  // Уходит текст или хотя бы одно вложение, но не пока файлы ещё грузятся
  const canSend = computed(
    () =>
      !isSending.value &&
      !!publisherId.value &&
      !attachments.value?.isUploading &&
      (text.value.trim() !== "" || (attachments.value?.files.length ?? 0) > 0)
  );

  const scrollToEnd = () => {
    nextTick(() => {
      if (listEl.value) listEl.value.scrollTo({ top: listEl.value.scrollHeight });
    });
  };

  const listenChat = (chatId: number) => {
    if (!socket.value) return;
    joinChat(chatId);
    if (isListeningMessages.value) return;
    handleTyping(advertiserId, "chat");
    handleNewMessage((data: any) => appendMessage(data), "chat");
  };

  // Сообщение приходит из двух мест: из ответа POST и эхом из сокета, и порядок
  // не гарантирован. Добавляем один раз — второй экземпляр отсекаем по messageId
  const appendMessage = (data: any) => {
    if (!data) return;
    if (data.messageId && messages.value.some((m: any) => m.messageId === data.messageId)) return;
    messages.value.push({
      ...data,
      id: data.id ?? data.messageId ?? messages.value.length + 1,
      created: data.created || dayjs().format("YYYY-MM-DD HH:mm:ss"),
      files: data.files ?? [],
      ...pickMessageMeta(data),
    });
    scrollToEnd();
  };

  const load = async () => {
    try {
      if (!publisherId.value) {
        const full = await PlaceService.getPlace(props.place.id, undefined, (props.place.social as any) || "telegram");
        publisherId.value = full?.userId ?? null;
      }
      if (!publisherId.value) return;
      const existing = unwrapChat(
        await ChatAdvertiserService.findChannelChat({ ...channelKey(), publisherId: publisherId.value, advertiserId })
      );
      if (existing?.id) {
        const data = (await ChatAdvertiserService.getChatMessagesById(existing.id)) as { messages: IMessageChat[] };
        // В карточке чата есть publications — по ним подписываем системные события
        chat.value = { ...existing, ...(data || {}) };
        messages.value = (data?.messages || []).reverse();
        ChatAdvertiserService.readAllMessages(existing.id);
        listenChat(existing.id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
      scrollToEnd();
    }
  };

  const onInput = () => {
    if (chat.value) setupTypingTimer(chat.value.id, "chat");
  };

  const send = async () => {
    const content = text.value.trim();
    const files = attachments.value?.files ?? [];
    if (!canSend.value || !publisherId.value) return;
    isSending.value = true;
    try {
      // Чат заводим только при первом сообщении, чтобы не плодить пустые диалоги.
      // Это канальный чат: в него же потом попадут публикации по этому каналу
      if (!chat.value) {
        chat.value = unwrapChat(
          await ChatAdvertiserService.getOrCreateChannelChat({
            ...channelKey(),
            publisherId: publisherId.value,
            advertiserId,
          })
        );
        if (!chat.value) throw new Error("channel chat not created");
        listenChat(chat.value.id);
      }
      const sent = await ChatAdvertiserService.sendMessage({
        chatId: chat.value.id,
        replyTo: null,
        userType: ChatUserType.ADVERTISER,
        content,
        status: "ACTIVE",
        files,
      });
      text.value = "";
      attachments.value?.reset();
      appendMessage(sent);
    } catch (error) {
      ElMessage.error("Не удалось отправить сообщение");
      console.error(error);
    } finally {
      isSending.value = false;
    }
  };

  onMounted(() => {
    connect(ApiKey.get());
    load();
  });

  onUnmounted(() => {
    disconnect(chat.value?.id || 0);
  });
</script>

<style lang="scss">
  .direct-chat-dialog {
    .el-dialog__header {
      display: none;
    }

    /* У обёртки кнопки display: contents, поэтому отступ ставим самой кнопке:
       скрепка по центру однострочного поля (44px) */
    &__attach > button {
      @apply mb-[11px];
    }

    /* Список выбранных файлов растягивается по ряду ввода, а у ряда нет боковых
       полей модалки — отступаем на её px-4, чтобы блок не прилипал к краям */
    &__attach > div {
      @apply left-4 right-4;
    }

    /* Поле с упоминаниями в виде этой модалки: серое, без рамки, до пяти строк.
       Селектор сильнее scoped-стилей самого поля */
    & &__input .mention-input__field .ProseMirror {
      @apply max-h-[124px] min-h-11 rounded-xl border-0 bg-bgnew px-4 py-3;

      p.is-editor-empty:first-child::before {
        @apply text-txmain/40;
      }
    }
  }
</style>
